import React, { useCallback, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import papa from 'papaparse'
import * as XLSX from 'xlsx'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { TableAdapter } from '@makyoapp/makyo-common'
import { Button, ButtonVariant, Field, Label, Modal, SelectDropdown, SimpleInput } from '@makyoapp/makyo-ui'

import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useGridImportData } from '@/hooks/useGridImportData'
import {
  ColumnHeader,
  generateUniqueTableName,
  guessColumnType,
  ImportItem,
  isValidFileType,
} from '@/utils/grid-import'
import { useGridV2Context } from '@/components/GridV2/contexts/GridV2Context'

// const MAX_FILE_ROWS_LIMIT = MAX_ROWS_LIMIT + 1 // add extra row for header
// const MAX_PREVIEW_ROWS = 21

function generateColumnHeaders(
  sheetData: string[][],
  type: 'import' | 'append',
  table: TableAdapter | undefined
): ColumnHeader[] {
  if (!table) return []
  if (!sheetData.length) return []
  if (!sheetData[0]) return []

  const firstRow = sheetData[0]
  if (!Array.isArray(firstRow)) return []

  // Find the most populated row (excluding header row)
  const mostPopulatedRow = sheetData.slice(1).reduce<{ row: string[]; count: number }>(
    (best, current) => {
      const nonEmptyCells = current.filter((cell) => cell?.toString().trim() !== '').length
      return nonEmptyCells > best.count ? { row: current, count: nonEmptyCells } : best
    },
    { row: [], count: 0 }
  ).row

  let columns: ColumnHeader[] = []

  if (type === 'import') {
    columns = firstRow.map((title, index) => ({
      id: `column${index}`,
      originalIndex: index,
      title: String(title),
      isHidden: false,
      columnType: guessColumnType(String(title), mostPopulatedRow[index] || ''),
    }))
  } else {
    // Keep track of already matched column IDs
    const matchedColumnIds = new Set<string>()

    const filteredColumns = table.columns.filter(
      (col) =>
        ![
          ColumnTypes.Slug,
          ColumnTypes.Function,
          ColumnTypes.Math,
          ColumnTypes.Template,
          ColumnTypes.SingleCell,
          ColumnTypes.Join,
          ColumnTypes.Formula,
          ColumnTypes.Connection,
          ColumnTypes.Lookup,
        ].includes(col.type)
    )

    columns = firstRow.map((title, index) => {
      // try to match column by the name
      const matchedColumn = filteredColumns.find((col) => {
        const isNameMatch =
          col.name.toLowerCase() === title.toLowerCase() ||
          title.toLowerCase().includes(col.name.toLowerCase()) ||
          col.name.toLowerCase().includes(title.toLowerCase())
        // Only match if column hasn't been matched before
        return isNameMatch && !matchedColumnIds.has(col.id)
      })

      // If we found a match, add it to our tracked set
      if (matchedColumn) {
        matchedColumnIds.add(matchedColumn.id)
      }

      return {
        id: matchedColumn ? matchedColumn.id : `column${index}`,
        originalIndex: index,
        title: String(title),
        isHidden: false,
        columnType: matchedColumn ? matchedColumn.type : guessColumnType(String(title), mostPopulatedRow[index] || ''),
      }
    })
  }

  return columns
}

/**
 * Grid Import: Select File step
 *
 * Handles file selection and upload for CSV import
 */
const GridImportSelectFile = () => {
  const { t } = useTranslation()

  // Local State
  const [fileUploadErrorMessage, setFileUploadErrorMessage] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [files, setFiles] = useState<File[]>([])

  const { tables } = useDbClientContext()
  const { importState, addImportItem, nextStep, setImportState } = useGridImportData()

  // get current table
  const { table } = useGridV2Context()

  // Excel file
  const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null)
  const [selectedSheetName, setSelectedSheetName] = useState('')

  const getAcceptedFileTypes = () => {
    return importState.fileType === 'excel' ? '.xlsx,.xls' : '.csv'
  }

  const getFileDescription = () => {
    return importState.fileType === 'excel'
      ? t('Only valid for Excel files (xlsx, xls)')
      : t('Only valid for CSV files')
  }

  const handleSelectSheet = (sheetName: string) => {
    setSelectedSheetName(sheetName)
    if (fileUploadErrorMessage) {
      setFileUploadErrorMessage(null)
    }
  }

  const handleProcessSheet = useCallback(() => {
    if (!selectedSheetName || !workbook) return

    setIsLoading(true)

    try {
      const wbSheetData = workbook.Sheets[selectedSheetName]

      const sheetDataJson = XLSX.utils.sheet_to_json(wbSheetData, {
        header: 1,
        raw: false,
        defval: '',
      }) as string[][]

      const cleanedData = sheetDataJson
        .map((row) => row.map((cell) => (cell ? cell.toString().trim() : '')))
        .filter((row) => row.some((cell) => cell !== ''))

      const isEmptySheet = cleanedData.length === 0
      if (isEmptySheet) {
        setFileUploadErrorMessage(t('Selected sheet is empty'))
        setIsLoading(false)
        return
      }

      const columns = generateColumnHeaders(cleanedData, importState.type, table)

      const validTableName = generateUniqueTableName(selectedSheetName, tables)

      addImportItem([
        {
          index: 0,
          name: validTableName,
          data: cleanedData,
          isIgnoreFirstRow: true,
          columnHeaders: columns,
          excludeColumnHeaders: [],
        },
      ])

      setIsLoading(false)

      nextStep()

      setWorkbook(null)
      setSelectedSheetName('')
    } catch (err) {
      console.error('Error processing sheet', err)
      setFileUploadErrorMessage('Error processing sheet')
      setIsLoading(false)
    }
    //
  }, [selectedSheetName, workbook, importState.type, table, tables, addImportItem, nextStep, t])

  const handleDismissSheetSelection = () => {
    setWorkbook(null)
    setSelectedSheetName('')
  }

  /**
   * Handles Excel file upload
   */
  const handleExcelFile = (file: File) => {
    setIsLoading(true)

    const reader = new FileReader()

    reader.onload = (e) => {
      try {
        const data = e.target?.result
        const workbook = XLSX.read(data, {
          type: 'array',
          cellDates: true,
          cellNF: false,
          cellText: false,
        })

        const isMultipleSheets = workbook.SheetNames.length > 1

        if (isMultipleSheets && importState.type === 'append') {
          // show sheet selection for user
          setWorkbook(workbook)
          setIsLoading(false)
          return
          //
        } else {
          const sheetsData = workbook.SheetNames.map((sheetName) => {
            const worksheet = workbook.Sheets[sheetName]
            return XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              raw: false,
              defval: '',
            }) as string[][]
          })

          // Clean and format the data for all sheets
          const cleanedData = sheetsData.map((sheetData) =>
            sheetData
              .map((row) => row.map((cell) => (cell ? String(cell).trim() : '')))
              .filter((row) => row.some((cell) => cell !== ''))
          )

          // Determine non-empty sheets
          const nonEmptyIndices = cleanedData
            .map((sheet, idx) => (sheet.length > 0 ? idx : -1))
            .filter((idx) => idx !== -1) as number[]

          if (nonEmptyIndices.length === 0) {
            setFileUploadErrorMessage(t('All sheets are empty'))
            setIsLoading(false)
            return
          }

          // if (cleanedData.some((sheet) => sheet.length > MAX_FILE_ROWS_LIMIT)) {
          //   setFileUploadErrorMessage(`One or more sheets contain too many rows (max ${MAX_FILE_ROWS_LIMIT})`)
          //   setIsLoading(false)
          //   return
          // }

          // Set sheet names with valid table names
          const validSheetNames = workbook.SheetNames.map((name) => generateUniqueTableName(name, tables))

          const importItems: ImportItem[] = []

          nonEmptyIndices.forEach((index: number) => {
            const sheet = cleanedData[index]
            importItems.push({
              index: index,
              name: validSheetNames[index],
              data: sheet,
              isIgnoreFirstRow: true,
              columnHeaders: generateColumnHeaders(sheet, importState.type, table),
              excludeColumnHeaders: [],
            })
          })

          addImportItem(importItems)

          nextStep()
        }

        //
      } catch (error) {
        setFileUploadErrorMessage('Error reading Excel file')
      } finally {
        setIsLoading(false)
      }
    }

    reader.readAsArrayBuffer(file)
  }

  /**
   * Handles CSV file upload
   */
  const handleCsvFile = async (file: File) => {
    setIsLoading(true)

    // CSV handling
    let fullRowsData: string[][] = []

    papa.parse(file, {
      header: false,
      skipEmptyLines: true,
      encoding: 'UTF-8',
      transformHeader: (header) => header.trim(),
      transform: (value) => (value ? value.toString().trim() : ''),
      // ? rows limit is disabled for now
      step: ({ data }, parser) => {
        fullRowsData.push(data as string[])

        // if (fullRowsData.length > MAX_FILE_ROWS_LIMIT) {
        //   parser.abort()
        //   fullRowsData = []
        //   setIsLoading(false)
        //   setFileUploadErrorMessage(`File contains too many rows, only ${MAX_FILE_ROWS_LIMIT} rows are allowed`)
        // }
      },
      complete: () => {
        const isEmptyFile = fullRowsData.length === 0
        if (isEmptyFile) {
          setFileUploadErrorMessage(t('Import Empty File'))
          setIsLoading(false)
          return
        }

        const cleanedData = fullRowsData.filter((row) => row.some((cell) => cell !== ''))

        const fileName = file.name.replace(/\.csv$/i, '')
        const validTableName = generateUniqueTableName(fileName, tables)

        const columns = generateColumnHeaders(cleanedData, importState.type, table)

        addImportItem([
          {
            index: importState.items.length,
            name: validTableName,
            data: cleanedData,
            isIgnoreFirstRow: true,
            columnHeaders: columns,
            excludeColumnHeaders: [],
          },
        ])

        setIsLoading(false)
        nextStep()
      },
      error: (error) => {
        setIsLoading(false)
        setFileUploadErrorMessage(error.message)
      },
    })
  }

  /**
   * Handles file selection and validates file name
   */
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFileUploadErrorMessage(null)
    const fileList = Array.from(event.target.files || [])
    if (fileList.length === 0) {
      return
    }

    const validations =
      importState.fileType === 'excel'
        ? {
            extensions: ['xlsx', 'xls'],
            mimeTypes: [
              'application/vnd.ms-excel',
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            ],
          }
        : {
            extensions: ['csv'],
            mimeTypes: ['text/csv', 'application/vnd.ms-excel'],
          }

    const allValid = fileList.every((f) => isValidFileType(f, validations))
    if (!allValid) {
      const errorMessage =
        importState.fileType === 'excel'
          ? 'Invalid file type, only Excel files (xlsx, xls) are allowed'
          : 'Invalid file type, only CSV file is allowed'
      setFileUploadErrorMessage(errorMessage)
      return
    }

    if (fileList.length === 1) {
      const fileName = fileList[0].name.replace(/\.[^/.]+$/, '')
      const validTableName = generateUniqueTableName(fileName, tables)
      setImportState((prevState) => ({
        ...prevState,
        fileName: validTableName,
      }))
    } else {
      setImportState((prevState) => ({
        ...prevState,
        fileName: `${fileList.length} files selected`,
      }))
    }

    setFiles(fileList)
  }

  /**
   * Handles file clear
   */
  const handleClearFile = () => {
    setImportState((prevState) => ({
      ...prevState,
      fileName: '',
      items: [],
    }))
    setFileUploadErrorMessage(null)
    setFiles([])
  }

  /**
   * Handles file submission
   * Parses CSV file and processes data
   */
  const handleFileSubmit = async () => {
    if (!files || files.length === 0) {
      setFileUploadErrorMessage('Please select a file!')
      return
    }

    if (files.length === 1) {
      if (importState.fileType === 'excel') {
        handleExcelFile(files[0])
      } else {
        handleCsvFile(files[0])
      }
      return
    }

    try {
      setIsLoading(true)

      if (importState.fileType === 'excel') {
        const readExcel = (file: File) =>
          new Promise<ImportItem[]>((resolve, reject) => {
            const reader = new FileReader()
            reader.onload = (e) => {
              try {
                const data = e.target?.result
                const workbook = XLSX.read(data as ArrayBuffer, {
                  type: 'array',
                  cellDates: true,
                  cellNF: false,
                  cellText: false,
                })

                const sheetsData = workbook.SheetNames.map((sheetName) => {
                  const worksheet = workbook.Sheets[sheetName]
                  return XLSX.utils.sheet_to_json(worksheet, {
                    header: 1,
                    raw: false,
                    defval: '',
                  }) as string[][]
                })

                const cleanedData = sheetsData.map((sheet) =>
                  sheet
                    .map((row) => row.map((cell) => (cell ? String(cell).trim() : '')))
                    .filter((row) => row.some((cell) => cell !== ''))
                )

                const baseName = file.name.replace(/\.[^/.]+$/, '')
                const validSheetNames = workbook.SheetNames.map((name) =>
                  generateUniqueTableName(`${baseName} - ${name}`, tables)
                )

                const items: ImportItem[] = []
                cleanedData.forEach((sheet, index) => {
                  items.push({
                    index: index,
                    name: validSheetNames[index],
                    data: sheet,
                    isIgnoreFirstRow: true,
                    columnHeaders: generateColumnHeaders(sheet, importState.type, table),
                    excludeColumnHeaders: [],
                  })
                })
                resolve(items)
              } catch (err) {
                reject(err)
              }
            }
            reader.onerror = reject
            reader.readAsArrayBuffer(file)
          })

        const results = await Promise.all(files.map((f) => readExcel(f)))
        const flattened = results.flat()
        const allItems = flattened.map((it, idx) => ({ ...it, index: idx }))
        addImportItem(allItems)
      } else {
        const parseCsv = (file: File) =>
          new Promise<ImportItem>((resolve, reject) => {
            let fullRowsData: string[][] = []
            papa.parse(file, {
              header: false,
              skipEmptyLines: true,
              encoding: 'UTF-8',
              transformHeader: (header) => header.trim(),
              transform: (value) => (value ? value.toString().trim() : ''),
              step: ({ data }) => {
                fullRowsData.push(data as string[])
              },
              complete: () => {
                const cleanedData = fullRowsData.filter((row) => row.some((cell) => cell !== ''))
                const fileName = file.name.replace(/\.csv$/i, '')
                const validTableName = generateUniqueTableName(fileName, tables)
                const columns = generateColumnHeaders(cleanedData, importState.type, table)
                resolve({
                  index: 0,
                  name: validTableName,
                  data: cleanedData,
                  isIgnoreFirstRow: true,
                  columnHeaders: columns,
                  excludeColumnHeaders: [],
                })
              },
              error: (error) => reject(error),
            })
          })

        const results = await Promise.all(files.map((f) => parseCsv(f)))
        const allItems = results.map((it, idx) => ({ ...it, index: idx }))
        addImportItem(allItems)
      }

      nextStep()
    } catch (err: any) {
      setFileUploadErrorMessage(err?.message || 'Error processing files')
    } finally {
      setIsLoading(false)
    }
  }

  // reset workbook (large data)
  // useEffect(() => {
  //   return () => {
  //     setWorkbook(null)
  //   }
  // }, [])

  return (
    <>
      {workbook ? (
        <React.Fragment>
          <Modal.Body>
            <div className='container w-[600px]'>
              <Field>
                <Label>Choose a sheet</Label>
                <div className='w-full'>
                  <SelectDropdown
                    toggleClassName='w-full border bdr'
                    value={selectedSheetName}
                    onChange={handleSelectSheet}
                    renderOption={(sheetName) => {
                      const sheetData = workbook.Sheets[sheetName]
                      const sheetDataJson = XLSX.utils.sheet_to_json(sheetData, {
                        header: 1,
                        raw: false,
                        defval: '',
                      }) as string[][]
                      const cleanedLen = sheetDataJson
                        .map((row) => row.map((cell) => (cell ? cell.toString().trim() : '')))
                        .filter((row) => row.some((cell) => cell !== '')).length
                      return (
                        <div className='flex-1 flex items-center justify-between gap-2 pr-2'>
                          <div>{sheetName}</div>
                          <div className='text-m-sm text-light'>({cleanedLen} rows)</div>
                        </div>
                      )
                    }}
                    options={workbook.SheetNames}
                    withSearch
                  />
                  {fileUploadErrorMessage && <div className='mk-input-error'>{fileUploadErrorMessage}</div>}
                </div>
              </Field>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <div className='flex justify-between w-full'>
              <Button onClick={handleDismissSheetSelection} variant={ButtonVariant.Outlined}>
                <Trans>Cancel</Trans>
              </Button>

              <Button onClick={handleProcessSheet} disabled={!selectedSheetName || !!fileUploadErrorMessage}>
                <Trans>Next</Trans>
              </Button>
            </div>
          </Modal.Footer>
        </React.Fragment>
      ) : (
        <Modal.Body>
          <div className='container w-[600px]'>
            <div className='flex flex-row'>
              <SimpleInput
                type='file'
                placeholder={importState.fileName || t('Select file') + '...'}
                description={getFileDescription()}
                showClear
                accept={getAcceptedFileTypes()}
                multiple
                onChange={handleFileChange}
                error={t(fileUploadErrorMessage || '')}
                onClear={handleClearFile}
                loading={isLoading}
                name='file'
              />
              <Button
                className='ml-5'
                disabled={!importState.fileName || fileUploadErrorMessage !== null}
                onClick={handleFileSubmit}
              >
                <Trans>Upload</Trans>
              </Button>
            </div>
          </div>
        </Modal.Body>
      )}
    </>
  )
}

export default GridImportSelectFile
